import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { Calendar, Download } from 'lucide-react';
import { toast } from 'sonner';

import { PackWeekTabs } from 'components/fbm/substitutions/PackWeekTabs';
import { buildPackWeekOptions } from 'components/fbm/substitutions/packWeekUtils';
import { buildSubstitutionRulesCsv } from 'components/fbm/substitutions/substitutionRulesCsv';
import { SubstitutionRulesTable } from 'components/fbm/substitutions/SubstitutionRulesTable';
import { UserContext } from 'contexts/UserContext';
import { Button } from 'shadcn/components/ui/Button';
import { useDeleteRuleGroupMutation } from 'shared/hooks/fbm/useDeleteRuleGroupMutation';
import { useSubstitutionRulesQuery } from 'shared/hooks/fbm/useSubstitutionRulesQuery';
import { useToggleRuleFacilityStatusMutation } from 'shared/hooks/fbm/useToggleRuleFacilityStatusMutation';
import { csvUtil } from 'shared/utils/csvUtil';

function ViewSubstitutionRules() {
    const { fbmAccount, user } = useContext(UserContext);
    const fbmAccountName = fbmAccount?.fbmAccountName;
    const facilityId = user?.facilityID;
    const facilityName = user?.facilityName;

    const packWeekOptions = useMemo(() => buildPackWeekOptions(), []);

    const packWeekByKey = useMemo(
        () => Object.fromEntries(packWeekOptions.map((option) => [option.key, option])),
        [packWeekOptions],
    );

    const [selectedPackWeek, setSelectedPackWeek] = useState('current');
    const { week, year } = packWeekByKey[selectedPackWeek];

    const { data, error, isFetching, isLoading } = useSubstitutionRulesQuery({
        enabled: !!fbmAccount?.id,
        fbmAccountName,
        facilityId,
        week,
        year,
    });

    const rows = useMemo(() => data ?? [], [data]);

    useEffect(() => {
        if (error) {
            toast.error(`No substitution rules found: ${error.message}`);
        }
    }, [error]);

    const { mutate: toggleRuleFacilityStatus } = useToggleRuleFacilityStatusMutation({
        onError: (toggleError) => {
            toast.error(toggleError.message ?? 'Failed to update substitution rule');
        },
        onSuccess: () => {
            toast.success('Substitution rule updated successfully');
        },
    });

    const handleToggleEnabled = useCallback(
        (rule) => {
            if (!facilityId) {
                toast.error('Please select a facility to toggle substitution rules');
                return;
            }

            if (!rule?.ruleGroupId) {
                toast.error('Unable to toggle rule: missing rule group ID');
                return;
            }

            const isEnabled = rule.isEnabled !== false;

            toggleRuleFacilityStatus({
                fbmAccountName,
                facilityId,
                isEnabled: !isEnabled,
                ruleGroupId: rule.ruleGroupId,
                week,
                year,
            });
        },
        [facilityId, fbmAccountName, toggleRuleFacilityStatus, week, year],
    );

    const { mutate: deleteRuleGroup } = useDeleteRuleGroupMutation({
        onError: (deleteError) => {
            toast.error(deleteError.message ?? 'Failed to delete substitution rule');
        },
        onSuccess: () => {
            toast.success('Substitution rule deleted successfully');
        },
    });

    const handleDeleteRule = useCallback(
        (rule) => {
            if (!rule?.ruleGroupId) {
                toast.error('Unable to delete rule: missing rule group ID');
                return;
            }

            deleteRuleGroup({
                fbmAccountName,
                facilityId,
                ruleGroupId: rule.ruleGroupId,
            });
        },
        [deleteRuleGroup, facilityId, fbmAccountName],
    );

    const handleDownloadCsv = useCallback(() => {
        if (rows.length === 0) {
            toast.error('No substitution rules to download');
            return;
        }

        const csvContent = buildSubstitutionRulesCsv(rows);
        const filename = `substitution_rules_${fbmAccountName}_week${week}_${year}.csv`;
        csvUtil.downloadCsv(csvContent, filename);
    }, [fbmAccountName, rows, week, year]);

    const title = fbmAccount?.companyName ? `${fbmAccount.companyName} - Substitution Rules` : 'Substitution Rules';

    if (!fbmAccountName) {
        return (
            <div className="mt-4">
                <h2 className="mb-4 text-lg font-semibold">{title}</h2>
                <p className="text-sm text-muted-foreground">
                    Please select an FBM account above to view substitution rules.
                </p>
            </div>
        );
    }

    return (
        <div className="mt-4 flex flex-col">
            <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                <h2 className="text-lg font-semibold">{title}</h2>
                <div className="flex flex-col items-stretch gap-3 sm:flex-row sm:items-end sm:gap-4">
                    <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        disabled={isLoading || rows.length === 0}
                        onClick={handleDownloadCsv}
                    >
                        <Download className="mr-2 h-4 w-4" />
                        Download CSV
                    </Button>
                    <div className="flex flex-col items-center gap-1">
                        <span className="inline-flex items-center gap-1 text-xs text-muted-foreground">
                            <Calendar className="h-3 w-3" />
                            Pack Week
                        </span>
                        <PackWeekTabs
                            className="gap-0"
                            options={packWeekOptions}
                            value={selectedPackWeek}
                            onValueChange={setSelectedPackWeek}
                        />
                    </div>
                </div>
            </div>
            <SubstitutionRulesTable
                data={rows}
                facilityName={facilityName}
                isLoading={isLoading}
                isFetching={isFetching}
                onToggleEnabled={handleToggleEnabled}
                onDelete={handleDeleteRule}
            />
        </div>
    );
}

export { ViewSubstitutionRules };
